import React, { useState } from "react";
import { FiCheck, FiCheckCircle, FiLock } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { OutlinedInput } from "../../../../../../components/OutlinedInput";
import { PrimaryButton } from "../../../../../../components/PrimaryButton";
import { SecondaryButton } from "../../../../../../components/SecondaryButton";
import { showToast } from "../../../../../../components/CustomToast";
import api from "../../../../../../services/api";
import { IPersonFormData, ILicenseeFormData, IAddressFormData } from "../../dtos.d";
import {
  SectionBlock,
  SectionHeader,
  SectionTitleWrapper,
  SectionTitleRow,
  SectionTitle,
  SectionSubtitle,
  PasswordForm,
  SuccessCard,
  SuccessIconCircle,
  SuccessTitle,
  SuccessSubtitle,
  Footer,
} from "./styles";

interface StepSuccessProps {
  token: string | undefined;
  isMockMode: boolean;
  personFormData: IPersonFormData;
  licenseeFormData: ILicenseeFormData;
  addressFormData: IAddressFormData;
  companyAddressFormData: IAddressFormData | undefined;
  registerCompanyAddress: boolean;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  prevStep: () => void;
  signOut: () => void;
  validateCode: (params: any) => Promise<any>;
}

export function StepSuccess({
  token,
  isMockMode,
  personFormData,
  licenseeFormData,
  addressFormData,
  companyAddressFormData,
  registerCompanyAddress,
  loading,
  setLoading,
  prevStep,
  signOut,
  validateCode,
}: StepSuccessProps) {
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [errors, setErrors] = useState<any>({});
  const [isCompleted, setIsCompleted] = useState(false);
  const navigate = useNavigate();

  const handleFinishRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: any = {};

    if (!password || password.length < 6) {
      newErrors.password = "A senha deve ter pelo menos 6 caracteres";
    }
    if (password !== passwordConfirmation) {
      newErrors.passwordConfirmation = "As senhas não coincidem";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showToast({ type: "error", message: "Verifique os erros nas senhas digitadas." });
      return;
    }

    setLoading(true);

    if (isMockMode) {
      // Simulate success flow in development/mock mode without calling actual API endpoints
      setTimeout(() => {
        setLoading(false);
        setIsCompleted(true);
        showToast({
          type: "success",
          message: "Cadastro simulado com sucesso (Modo Mock de Desenvolvimento)!",
        });
      }, 1500);
      return;
    }

    try {
      // 1. Create User
      const createdUser = await validateCode({
        email: licenseeFormData.email,
        password,
        registration_token: token,
      });

      if (!createdUser || !createdUser.id) {
        throw new Error("Falha na criação do usuário.");
      }

      // Ensure Authorization token header is attached
      const storedToken = localStorage.getItem("@FixitApp:token");
      if (storedToken) {
        api.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
      }

      // 2. Create Person
      const personBody = {
        user: { id: createdUser.id },
        name: personFormData.name,
        cpf: personFormData.cpf.replace(/[^\d]/g, ""),
        country: personFormData.country?.id || "BRA",
        cellphone: personFormData.cellphone.replace(/[^\d]/g, ""),
        state: personFormData.state,
        city: personFormData.city,
        expertise: personFormData.expertise,
        professional_number: personFormData.professional_number || "",
      };

      const personResponse = await api.post("/person", personBody);
      const personId = personResponse.data.id;

      if (!personId) {
        throw new Error("Falha ao registrar dados pessoais.");
      }

      // 3. Create Licensee
      const licenseeBody = {
        person: { id: personId },
        printers: [],
        instagram: "",
        email: licenseeFormData.email,
        country: licenseeFormData.country?.id || "BRA",
        phone: (licenseeFormData.phone || personFormData.cellphone).replace(/[^\d]/g, ""),
        document_number: (licenseeFormData.cnpj || personFormData.cpf).replace(/[^\d]/g, ""),
        situation: "onboarding",
        city: licenseeFormData.cnpj ? licenseeFormData.city : personFormData.city,
        state: licenseeFormData.cnpj ? licenseeFormData.state : personFormData.state,
        name: licenseeFormData.cnpj ? licenseeFormData.fantasyName : personFormData.name,
        manager_name: personFormData.name,
        technical_manager: personFormData.name,
        technical_council: personFormData.professional_number || "",
        corporate_reason: licenseeFormData.companyName || personFormData.name,
      };

      const licenseeResponse = await api.post("/licensees", licenseeBody);
      const licenseeId = licenseeResponse.data.id;

      if (!licenseeId) {
        throw new Error("Falha ao criar credencial de Licenciado.");
      }

      // 4. Create Main Address (Delivery / Operation)
      const mainAddressBody = {
        licensee_id: licenseeId,
        address: addressFormData.address,
        number: addressFormData.noNumber ? "S/N" : addressFormData.number,
        complement: addressFormData.noComplement ? "" : addressFormData.complement || "",
        cep: addressFormData.cep.replace(/[^\d]/g, ""),
        district: addressFormData.district,
        city: addressFormData.city,
        state: addressFormData.state,
        country: addressFormData.country?.id || "BRA",
        type: "work",
      };
      await api.post("/licensee-addresses", mainAddressBody);

      // 4b. Create Billing Address if enabled
      if (registerCompanyAddress && companyAddressFormData) {
        const billingAddressBody = {
          licensee_id: licenseeId,
          address: companyAddressFormData.address,
          number: companyAddressFormData.noNumber ? "S/N" : companyAddressFormData.number,
          complement: companyAddressFormData.noComplement ? "" : companyAddressFormData.complement || "",
          cep: companyAddressFormData.cep.replace(/[^\d]/g, ""),
          district: companyAddressFormData.district,
          city: companyAddressFormData.city,
          state: companyAddressFormData.state,
          country: companyAddressFormData.country?.id || "BRA",
          type: "billing",
        };
        await api.post("/licensee-addresses", billingAddressBody);
      }

      // 5. Accept Terms
      try {
        const termsResponse = await api.get("/terms/current");
        if (termsResponse.data && termsResponse.data.id) {
          await api.post("/terms/accept", {
            term_id: termsResponse.data.id,
            user_id: createdUser.id,
          });
        }
      } catch (termErr) {
        console.warn("Erro ao aceitar termos automáticos. Pulando...", termErr);
      }

      // 6. Sign out to enforce clean login
      signOut();
      
      setIsCompleted(true);
      showToast({
        type: "success",
        message: "Licenciamento cadastrado e ativado com absoluto sucesso!",
      });
    } catch (err: any) {
      console.error("Cadastro final falhou: ", err);
      showToast({
        type: "error",
        message: err.response?.data?.message || "Ocorreu um erro ao finalizar seu cadastro. Por favor, tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLogin = () => {
    signOut();
    navigate("/login");
  };

  if (isCompleted) {
    return (
      <SectionBlock style={{ alignItems: "center", textAlign: "center", padding: "40px 24px" }}>
        <SuccessCard>
          <SuccessIconCircle>
            <FiCheck />
          </SuccessIconCircle>
          <SuccessTitle>Cadastro Ativado com Sucesso!</SuccessTitle>
          <SuccessSubtitle>
            Parabéns! Sua conta de licenciado Fix it foi criada e ativada. Você já faz parte da maior rede de tecnologia 3D em imobilização ortopédica!
          </SuccessSubtitle>
          <PrimaryButton
            type="button"
            text="Acessar Plataforma Fix it"
            onClick={handleGoToLogin}
            style={{ minWidth: "220px" }}
          />
        </SuccessCard>
      </SectionBlock>
    );
  }

  return (
    <SectionBlock>
      <SectionHeader>
        <SectionTitleWrapper>
          <SectionTitleRow>
            <SectionTitle>
              <FiLock /> Ativação de Acesso & Criação de Senha
            </SectionTitle>
          </SectionTitleRow>
          <SectionSubtitle>
            Crie sua senha de segurança para acessar o sistema administrativo, gerenciar pedidos e acompanhar sua assinatura.
          </SectionSubtitle>
        </SectionTitleWrapper>
      </SectionHeader>

      <PasswordForm onSubmit={handleFinishRegistration}>
        <OutlinedInput
          inputName="password"
          label="Defina sua senha (mínimo 6 dígitos)"
          type="password"
          handleChange={(val) => {
            setPassword(val);
            if (errors.password) setErrors((prev: any) => ({ ...prev, password: "" }));
          }}
          value={password}
          error={errors.password}
        />

        <OutlinedInput
          inputName="password_confirmation"
          label="Confirme sua senha"
          type="password"
          handleChange={(val) => {
            setPasswordConfirmation(val);
            if (errors.passwordConfirmation) setErrors((prev: any) => ({ ...prev, passwordConfirmation: "" }));
          }}
          value={passwordConfirmation}
          error={errors.passwordConfirmation}
        />

        <Footer style={{ marginTop: "16px" }}>
          <SecondaryButton
            type="button"
            text="Voltar"
            onClick={prevStep}
            disabled={loading}
            style={{ minWidth: "120px" }}
          />

          <PrimaryButton
            type="submit"
            text="Ativar e Concluir Conta"
            loading={loading}
            disabled={loading}
            style={{ minWidth: "220px" }}
          />
        </Footer>
      </PasswordForm>
    </SectionBlock>
  );
}
